import React, { useRef, useEffect } from "react";
import gsap from "gsap";

interface LetterRevealProps {
  text: string;
  as?: "h1" | "h2" | "h3" | "p" | "span";
  className?: string;
  delay?: number;
  stagger?: number;
  duration?: number;
}

export const LetterReveal: React.FC<LetterRevealProps> = ({
  text,
  as: Tag = "span",
  className = "",
  delay = 0,
  stagger = 0.03,
  duration = 0.8,
}) => {
  const containerRef = useRef<HTMLElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || hasAnimated.current) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const letters = container.querySelectorAll<HTMLSpanElement>(".letter");

    if (prefersReduced) {
      letters.forEach((el) => {
        el.style.opacity = "1";
        el.style.transform = "none";
      });
      return;
    }

    gsap.set(letters, { y: 40, opacity: 0 });
    gsap.to(letters, {
      y: 0,
      opacity: 1,
      duration,
      stagger,
      delay,
      ease: "expo.out",
      onComplete: () => {
        hasAnimated.current = true;
      },
    });

    return () => {
      gsap.killTweensOf(letters);
    };
  }, [delay, stagger, duration]);

  const chars = text.split("").map((char, i) => (
    <span key={i} className="letter inline-block" style={{ opacity: 0 }}>
      {char === " " ? "\u00A0" : char}
    </span>
  ));

  return (
    <Tag ref={containerRef as React.Ref<any>} className={className}>
      {chars}
    </Tag>
  );
};
